/**
 * @file src/helpers/browser/configureIosViewport.ts
 * @exports configureIosViewport
 * @summary Prevents iOS browsers from automatically zooming the page when a small form input receives focus.
 */

type ConfigureIosViewportOptions = {
	documentObject: Document;
	navigatorObject: Pick<Navigator, "maxTouchPoints" | "platform" | "userAgent">;
};

const maximumScaleDirective = "maximum-scale=1";

const isIosWebKitDevice = ({
	maxTouchPoints,
	platform,
	userAgent,
}: ConfigureIosViewportOptions["navigatorObject"]): boolean => {
	const isIos = /iPad|iPhone|iPod/.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1);
	return isIos && /AppleWebKit/.test(userAgent);
};

/**
 * Adds an iOS-only viewport scale limit that suppresses automatic input-focus zoom.
 * Current iOS WebKit continues to allow user-initiated pinch zoom while honoring this directive for focus behavior.
 */
export function configureIosViewport({ documentObject, navigatorObject }: ConfigureIosViewportOptions): void {
	if (!isIosWebKitDevice(navigatorObject)) return;

	const viewport = documentObject.querySelector<HTMLMetaElement>('meta[name="viewport"]');
	if (!viewport) return;

	const directives = viewport.content
		.split(",")
		.map((directive) => directive.trim())
		.filter((directive) => directive && !directive.startsWith("maximum-scale="));

	viewport.content = [...directives, maximumScaleDirective].join(", ");
}
